import React, { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import { cn } from '@bem-react/classname'

import { Memo } from '@/hoc/Memo'
import { loadingSelector } from '@/core/services/Loading/store/loading.selector'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { changeLoading } from '@/core/services/Loading/store/loading.slice'

import './PrivacyPage.scss'

const cnPrivacy = cn('Privacy')

export const PrivacyPage: FC = Memo(() => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isLoading = useAppSelector(loadingSelector)

  useEffect(() => {
    if (isLoading) {
      dispatch(changeLoading(false))
    }
  }, [isLoading])

  return (
    <>
      <Helmet>
        <title>{t('Privacy Policy')} | DTM</title>
      </Helmet>
      <div className={cnPrivacy()}>
        <div>
          <div className={cnPrivacy('title')}>
            {t('Privacy Policy of ')}
            <b>{t('registration.dinamicagenerale.com')}</b>
          </div>
          <div>
            {t('This Application collects some Personal Data from its Users.')}
          </div>
          <hr className={cnPrivacy('border')} />
          <div className={cnPrivacy('header')}>
            {t(
              'Personal Data processed for the following purposes and using the following services:',
            )}
          </div>
          <div className={cnPrivacy('flex')}>
            <div className={cnPrivacy('left')}>
              <img src='/img/registr-privacy.png' alt='Registration' />
            </div>
            <div className={cnPrivacy('right')}>
              <div className={cnPrivacy('subheader')}>
                {t('Handling payments')}
              </div>
              <div className={cnPrivacy('subheader')}>{t('Stripe')}</div>
              <div>
                {t(
                  'Personal Data: billing address; email address; first name;last name; payment info; purchase history',
                )}
              </div>
            </div>
          </div>

          <hr className={cnPrivacy('border')} />
          <div className={cnPrivacy('header')}>{t('Contact information')}</div>
          <div className={cnPrivacy('flex')}>
            <div className={cnPrivacy('left')}>
              <img src='/img/registr-owner.png' alt='Registration' />
            </div>
            <div className={cnPrivacy('right')}>
              <div className={cnPrivacy('subheader')}>
                {t('Owner and Data Controller')}
              </div>
              <div>{t('Dinamica Generale S.p.A.')}</div>
              <div>{t('Via Mondadori, 15 - 46025 - Poggio Rusco (MN)')}</div>
              <div>{t('Company registration number 01599500202')}</div>
              <div>{t('REA MN 169392')}</div>
              <div>{t('Paid-in capital 1.000.000,00 I.V.')}</div>
              <div className={cnPrivacy('subheader')}>
                {t('Owner contact email')}
              </div>
              <a href='mailto: privacy@dinamicagenerale.com'>
                {t('privacy@dinamicagenerale.com')}
              </a>
            </div>
          </div>

          <hr className={cnPrivacy('border')} />
          <div className={cnPrivacy('header')}>
            {t('Types of Data collected')}
          </div>
          <div>
            {t(
              'Among the types of Personal Data that this Application collects, by itself or through third parties, there are: payment info; first name; last name; email address; billing address; purchase history.',
            )}
          </div>
          <div>
            {t(
              'Complete details on each type of Personal Data collected are provided in the dedicated sections of this privacy policy or by specific explanation texts displayed prior to the Data collection.Personal Data may be freely provided by the User, or, in case of Usage Data, collected automatically when using this Application.',
            )}
          </div>
          <div>
            {t(
              'Unless specified otherwise, all Data requested by this Application is mandatory and failure to provide this Data may make it impossible for this Application to provide its services. In cases where this Application specifically states that some Data is not mandatory, Users are free not to communicate this Data without consequences to the availability or the functioning of the Service.',
            )}
          </div>
          <div>
            {t(
              'Users who are uncertain about which Personal Data is mandatory are welcome to contact the Owner.Any use of Cookies – or of other tracking tools — by this Application or by the owners of third-party services used by this Application serves the purpose of providing the Service required by the User, in addition to any other purposes described in the present document and in the Cookie Policy, if available.',
            )}
          </div>
          <div>
            {t(
              'Users are responsible for any third-party Personal Data obtained, published or shared through this Application and confirm that they have the third partys consent to provide the Data to the Owner.',
            )}
          </div>
          <hr className={cnPrivacy('border')} />
          <div className={cnPrivacy('header')}>
            {t('Mode and place of processing the Data')}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('Methods of processing')}
          </div>
          <div>
            {t(
              'The Owner takes appropriate security measures to prevent unauthorized access, disclosure, modification, or unauthorized destruction of the Data.The Data processing is carried out using computers and/or IT enabled tools, following organizational procedures and modes strictly related to the purposes indicated. In addition to the Owner, in some cases, the Data may be accessible to certain types of persons in charge, involved with the operation of this Application (administration, sales, marketing, legal, system administration) or external parties (such as third-party technical service providers, mail carriers, hosting providers, IT companies, communications agencies) appointed, if necessary, as Data Processors by the Owner. The updated list of these parties may be requested from the Owner at any time.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('Legal basis of processing')}
          </div>
          <div>
            {t(
              'The Owner may process Personal Data relating to Users if one of the following applies',
            )}
          </div>
          <ul>
            <li>
              {t(
                'Users have given their consent for one or more specific purposes. Note: Under some legislations the Owner may be allowed to process Personal Data until the User objects to such processing (“opt-out”), without having to rely on consent or any other of the following legal bases. This, however, does not apply, whenever the processing of Personal Data is subject to European data protection law;',
              )}
            </li>
            <li>
              {t(
                'provision of Data is necessary for the performance of an agreement with the User and/or for any pre-contractual obligations thereof;',
              )}
            </li>
            <li>
              {t(
                'processing is necessary for compliance with a legal obligation to which the Owner is subject;',
              )}
            </li>
            <li>
              {t(
                'processing is related to a task that is carried out in the public interest or in the exercise of official authority vested in the Owner;',
              )}
            </li>
            <li>
              {
                'processing is necessary for the purposes of the legitimate interests pursued by the Owner or by a third party.'
              }
            </li>
          </ul>
          <div>
            {t(
              'In any case, the Owner will gladly help to clarify the specific legal basis that applies to the processing, and in particular whether the provision of Personal Data is a statutory or contractual requirement, or a requirement necessary to enter into a contract.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>{t('Place')}</div>
          <div>
            {t(
              'The Data is processed at the Owners operating offices and in any other places where the parties involved in the processing are located.',
            )}
          </div>
          <div>
            {t(
              'Depending on the Users location, data transfers may involve transferring the Users Data to a country other than their own. To find out more about the place of processing of such transferred Data, Users can check the section containing details about the processing of Personal Data.',
            )}
          </div>
          <div>
            {t(
              'Users are also entitled to learn about the legal basis of Data transfers to a country outside the European Union or to any international organization governed by public international law or set up by two or more countries, such as the UN, and about the security measures taken by the Owner to safeguard their Data.',
            )}
          </div>
          <div>
            {t(
              'If any such transfer takes place, Users can find out more by checking the relevant sections of this document or inquire with the Owner using the information provided in the contact section.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>{t('Retention time')}</div>
          <div>
            {t(
              'Personal Data shall be processed and stored for as long as required by the purpose they have been collected for.',
            )}
          </div>
          <div>{t('Therefore')}</div>
          <ul>
            <li>
              {t(
                'Personal Data collected for purposes related to the performance of a contract between the Owner and the User shall be retained until such contract has been fully performed.',
              )}
            </li>
            <li>
              {t(
                'Personal Data collected for the purposes of the Owner’s legitimate interests shall be retained as long as needed to fulfill such purposes. Users may find specific information regarding the legitimate interests pursued by the Owner within the relevant sections of this document or by contacting the Owner.',
              )}
            </li>
          </ul>
          <div>
            {t(
              'The Owner may be allowed to retain Personal Data for a longer period whenever the User has given consent to such processing, as long as such consent is not withdrawn. Furthermore, the Owner may be obliged to retain Personal Data for a longer period whenever required to do so for the performance of a legal obligation or upon order of an authority.',
            )}
          </div>
          <div>
            {t(
              'Once the retention period expires, Personal Data shall be deleted. Therefore, the right of access, the right to erasure, the right to rectification and the right to data portability cannot be enforced after expiration of the retention period.',
            )}
          </div>
          <hr className={cnPrivacy('border')} />
          <div className={cnPrivacy('header')}>
            {t('The purposes of processing')}
          </div>
          <div>
            {t(
              'The Data concerning the User is collected to allow the Owner to provide its Service, comply with its legal obligations, respond to enforcement requests, protect its rights and interests (or those of its Users or third parties), detect any malicious or fraudulent activity, as well as the following: Handling payments.',
            )}
          </div>
          <div>
            {t(
              'For specific information about the Personal Data used for each purpose, the User may refer to the section “Detailed information on the processing of Personal Data”.',
            )}
          </div>
          <hr className={cnPrivacy('border')} />
          <div className={cnPrivacy('header')}>
            {t('Detailed information on the processing of Personal Data')}
          </div>
          <div>
            {t(
              'Personal Data is collected for the following purposes and using the following services:',
            )}
          </div>
          <div>
            {t(
              'Unless otherwise specified, this Application processes any payments by credit card, bank transfer or other means via external payment service providers. In general and unless where otherwise stated, Users are requested to provide their payment details and personal information directly to such payment service providers. This Application isnt involved in the collection and processing of such information: instead, it will only receive a notification by the relevant payment service provider as to whether payment has been successfully completed.',
            )}
          </div>
          <div>{t('Stripe (Stripe Payments Europe, Limited)')}</div>
          <div className={cnPrivacy('subheader')}>
            {t(
              'Stripe is a payment service provided by Stripe Payments Europe, Limited.',
            )}
          </div>
          <div>
            {t(
              'Personal Data processed: billing address; email address; first name; last name; payment info; purchase history.',
            )}
          </div>
          <div>
            {t('Place of processing: European Union – Privacy Policy.')}
          </div>
          <hr className={cnPrivacy('border')} />
          <div className={cnPrivacy('header')}>{t('The rights of Users')}</div>
          <div>
            {t(
              'Users may exercise certain rights regarding their Data processed by the Owner.',
            )}
          </div>
          <div>
            {t(
              'In particular, Users have the right to do the following, to the extent permitted by law:',
            )}
          </div>
          <ul>
            <li>
              {t(
                'Withdraw their consent at any time. Users have the right to withdraw consent where they have previously given their consent to the processing of their Personal Data.',
              )}
            </li>
            <li>
              {t(
                'Object to processing of their Data. Users have the right to object to the processing of their Data if the processing is carried out on a legal basis other than consent. Further details are provided in the dedicated section below.',
              )}
            </li>
            <li>
              {t(
                'Access their Data. Users have the right to learn if Data is being processed by the Owner, obtain disclosure regarding certain aspects of the processing and obtain a copy of the Data undergoing processing.',
              )}
            </li>
            <li>
              {t(
                'Verify and seek rectification. Users have the right to verify the accuracy of their Data and ask for it to be updated or corrected.',
              )}
            </li>
            <li>
              {t(
                'Restrict the processing of their Data. Users have the right to restrict the processing of their Data. In this case, the Owner will not process their Data for any purpose other than storing it.',
              )}
            </li>
            <li>
              {t(
                'Have their Personal Data deleted or otherwise removed. Users have the right to obtain the erasure of their Data from the Owner.',
              )}
            </li>
            <li>
              {t(
                'Receive their Data and have it transferred to another controller. Users have the right to receive their Data in a structured, commonly used and machine readable format and, if technically feasible, to have it transmitted to another controller without any hindrance.',
              )}
            </li>
            <li>
              {t(
                'Lodge a complaint. Users have the right to bring a claim before their competent data protection authority.',
              )}
            </li>
          </ul>
          <div className={cnPrivacy('subheader')}>
            {t('Details about the right to object to processing')}
          </div>
          <div>
            {t(
              'Where Personal Data is processed for a public interest, in the exercise of an official authority vested in the Owner or for the purposes of the legitimate interests pursued by the Owner, Users may object to such processing by providing a ground related to their particular situation to justify the objection.',
            )}
          </div>
          <div>
            {t(
              'Users must know that, however, should their Personal Data be processed for direct marketing purposes, they can object to that processing at any time, free of charge and without providing any justification. Where the User objects to processing for direct marketing purposes, the Personal Data will no longer be processed for such purposes. To learn whether the Owner is processing Personal Data for direct marketing purposes, Users may refer to the relevant sections of this document.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('How to exercise these rights')}
          </div>
          <div>
            {t(
              'Any requests to exercise User rights can be directed to the Owner through the contact details provided in this document. These requests can be exercised free of charge and will be answered by the Owner as early as possible and always within one month, providing Users with the information required by law. Any rectification or erasure of Personal Data or restriction of processing will be communicated by the Owner to each recipient, if any, to whom the Personal Data has been disclosed unless this proves impossible or involves disproportionate effort. At the Users’ request, the Owner will inform them about those recipients.',
            )}
          </div>
          <hr className={cnPrivacy('border')} />
          <div className={cnPrivacy('header')}>{t('Cookie Policy')}</div>
          <div>
            {t(
              'This Application uses Trackers. To learn more, the User may consult the Cookie Policy.',
            )}
          </div>
          <hr className={cnPrivacy('border')} />
          <div className={cnPrivacy('header')}>
            {t('Additional information about Data collection and processing')}
          </div>
          <div className={cnPrivacy('subheader')}>{t('Legal action')}</div>
          <div>
            {t(
              'The Users Personal Data may be used for legal purposes by the Owner in Court or in the stages leading to possible legal action arising from improper use of this Application or the related Services.The User declares to be aware that the Owner may be required to reveal personal data upon request of public authorities.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('Additional information about Users Personal Data')}
          </div>
          <div>
            {t(
              'In addition to the information contained in this privacy policy, this Application may provide the User with additional and contextual information concerning particular Services or the collection and processing of Personal Data upon request.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('System logs and maintenance')}
          </div>
          <div>
            {t(
              'For operation and maintenance purposes, this Application and any third-party services may collect files that record interaction with this Application (System logs) or use other Personal Data (such as the IP Address) for this purpose.',
            )}
          </div>
          <div>{t('System logs and maintenance')}</div>
          <div>
            {t(
              'For operation and maintenance purposes, this Application and any third-party services may collect files that record interaction with this Application (System logs) or use other Personal Data (such as the IP Address) for this purpose.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('Information not contained in this policy')}
          </div>
          <div>
            {t(
              'More details concerning the collection or processing of Personal Data may be requested from the Owner at any time. Please see the contact information at the beginning of this document.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('How “Do Not Track” requests are handled')}
          </div>
          <div>
            {t(
              'This Application does not support “Do Not Track” requests.To determine whether any of the third-party services it uses honor the “Do Not Track” requests, please read their privacy policies.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('Changes to this privacy policy')}
          </div>
          <div>
            {t(
              'The Owner reserves the right to make changes to this privacy policy at any time by notifying its Users on this page and possibly within this Application and/or - as far as technically and legally feasible - sending a notice to Users via any contact information available to the Owner. It is strongly recommended to check this page often, referring to the date of the last modification listed at the bottom.',
            )}
          </div>
          <div>
            {t(
              'Should the changes affect processing activities performed on the basis of the User’s consent, the Owner shall collect new consent from the User, where required.',
            )}
          </div>

          <div className={cnPrivacy('header')}>
            {t('Cookie Policy of registration.dinamicagenerale.com')}
          </div>
          <div>
            {t(
              'This document informs Users about the technologies that help this Application to achieve the purposes described below. Such technologies allow the Owner to access and store information (for example by using a Cookie) or use resources (for example by running a script) on a User’s device as they interact with this Application.',
            )}
          </div>
          <div>
            {t(
              'For simplicity, all such technologies are defined as "Trackers" within this document – unless there is a reason to differentiate.',
            )}
          </div>
          <div>
            {t(
              'For example, while Cookies can be used on both web and mobile browsers, it would be inaccurate to talk about Cookies in the context of mobile apps as they are a browser-based Tracker. For this reason, within this document, the term Cookies is only used where it is specifically meant to indicate that particular type of Tracker.',
            )}
          </div>
          <div>
            {t(
              'Some of the purposes for which Trackers are used may also require the Users consent. Whenever consent is given, it can be freely withdrawn at any time following the instructions provided in this document.',
            )}
          </div>
          <div>
            {t(
              'This Application only uses Trackers managed directly by the Owner (so-called “first-party” Trackers).The validity and expiration periods of first-party Cookies and other similar Trackers may vary depending on the lifetime set by the Owner. Some of them expire upon termination of the User’s browsing session.',
            )}
          </div>
          <div className={cnPrivacy('header')}>
            {t(
              'Activities strictly necessary for the operation of this Application and delivery of the Service',
            )}
          </div>
          <div>
            {t(
              'This Application uses so-called “technical” Cookies and other similar Trackers to carry out activities that are strictly necessary for the operation or delivery of the Service.',
            )}
          </div>
          <div className={cnPrivacy('header')}>
            {t('How to manage preferences and provide or withdraw consent')}
          </div>
          <div>
            {t(
              'There are various ways to manage Tracker related preferences and to provide and withdraw consent, where relevant',
            )}
          </div>
          <div>
            {t(
              'Users can manage preferences related to Trackers from directly within their own device settings, for example, by preventing the use or storage of Trackers.',
            )}
          </div>
          <div>
            {t(
              'Additionally, whenever the use of Trackers is based on consent, Users can provide or withdraw such consent by setting their preferences within the cookie notice or by updating such preferences accordingly via the relevant consent-preferences widget, if available.',
            )}
          </div>
          <div>
            {t(
              'It is also possible, via relevant browser or device features, to delete previously stored Trackers, including those used to remember the User’s initial consent.',
            )}
          </div>
          <div>
            {t(
              'Other Trackers in the browser’s local memory may be cleared by deleting the browsing history.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('Locating Tracker Settings')}
          </div>
          <div>
            {t(
              'Users can, for example, find information about how to manage Cookies in the most commonly used browsers at the following addresses',
            )}
          </div>
          <ul>
            <li>{t('Google Chrome')}</li>
            <li>{t('Mozilla Firefox')}</li>
            <li>{t('Apple Safari')}</li>
            <li>{t('Microsoft Internet Explorer')}</li>
            <li>{t('Microsoft Edge')}</li>
            <li>{t('Brave')}</li>
            <li>{t('Opera')}</li>
          </ul>
          <div>
            {t(
              'Users may also manage certain categories of Trackers used on mobile apps by opting out through relevant device settings such as the device advertising settings for mobile devices, or tracking settings in general (Users may open the device settings and look for the relevant setting).',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('Consequences of denying consent')}
          </div>
          <div>
            {t(
              'Users are free to decide whether or not to grant consent. However, please note that Trackers help this Application to provide a better experience and advanced functionalities to Users (in line with the purposes outlined in this document). Therefore, in the absence of the Users consent, the Owner may be unable to provide related features.',
            )}
          </div>

          <div className={cnPrivacy('header')}>
            {t('Owner and Data Controller')}
          </div>
          <div>{t('Dinamica Generale S.p.A.')}</div>
          <div>{t('Via Mondadori, 15 - 46025 - Poggio Rusco (MN)')}</div>
          <div>{t('company registration number 01599500202')}</div>
          <div>{t('REA MN 169392')}</div>
          <div>{t('Paid-in capital 1.000.000,00 I.V.')}</div>
          <div className={cnPrivacy('subheader')}>
            {t('Owner contact email')}
          </div>
          <div>{t('privacy@dinamicagenerale.com')}</div>
          <div>
            {t(
              'Given the objective complexity surrounding tracking technologies, Users are encouraged to contact the Owner should they wish to receive any further information on the use of such technologies by this Application.',
            )}
          </div>

          <div className={cnPrivacy('subheader')}>
            {t('Definitions and legal references')}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('Personal Data (or Data)')}
          </div>
          <div>
            {t(
              'Any information that directly, indirectly, or in connection with other information — including a personal identification number — allows for the identification or identifiability of a natural person.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>{t('Usage Data')}</div>
          <div>
            {t(
              'Information collected automatically through this Application (or third-party services employed in this Application), which can include: the IP addresses or domain names of the computers utilized by the Users who use this Application, the URI addresses (Uniform Resource Identifier), the time of the request, the method utilized to submit the request to the server, the size of the file received in response, the numerical code indicating the status of the servers answer (successful outcome, error, etc.), the country of origin, the features of the browser and the operating system utilized by the User, the various time details per visit (e.g., the time spent on each page within the Application) and the details about the path followed within the Application with special reference to the sequence of pages visited, and other parameters about the device operating system and/or the Users IT environment.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>{t('User')}</div>
          <div>
            {t(
              'The individual using this Application who, unless otherwise specified, coincides with the Data Subject.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>{t('Data Subject')}</div>
          <div>{t('The natural person to whom the Personal Data refers.')}</div>
          <div className={cnPrivacy('subheader')}>
            {t('Data Processor (or Data Supervisor)')}
          </div>
          <div>
            {t(
              'The natural or legal person, public authority, agency or other body which processes Personal Data on behalf of the Controller, as described in this privacy policy.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('Data Controller (or Owner)')}
          </div>
          <div>
            {t(
              'The natural or legal person, public authority, agency or other body which, alone or jointly with others, determines the purposes and means of the processing of Personal Data, including the security measures concerning the operation and use of this Application. The Data Controller, unless otherwise specified, is the Owner of this Application.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>{t('This Application')}</div>
          <div>
            {t(
              'The means by which the Personal Data of the User is collected and processed.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>{t('Service')}</div>
          <div>
            {t(
              'The service provided by this Application as described in the relative terms (if available) and on this site/application.',
            )}
          </div>
          <div className={cnPrivacy('subheader')}>
            {t('European Union (or EU)')}
          </div>
          <div>
            {t(
              'Unless otherwise specified, all references made within this document to the European Union include all current member states to the European Union and the European Economic Area.',
            )}
          </div>
          <hr className={cnPrivacy('border')} />
          <div className={cnPrivacy('subheader')}>{t('Legal information')}</div>
          <div>
            {t(
              'This privacy statement has been prepared based on provisions of multiple legislations, including Art. 13/14 of Regulation (EU) 2016/679 (General Data Protection Regulation).',
            )}
          </div>
          <div>
            {t(
              'This privacy policy relates solely to this Application, if not stated otherwise within this document.',
            )}
          </div>
          <hr className={cnPrivacy('border')} />
          <div>{t('Latest update: April 21, 2023')}</div>
          <div>
            {t(
              'iubenda hosts this content and only collects the Personal Data strictly necessary for it to be provided.',
            )}
          </div>
        </div>
      </div>
    </>
  )
})
